import React, { useEffect, useState } from 'react';
import api from '../services/api';
import {
  CheckCircle2,
  Clock3,
  Download,
  FileSpreadsheet,
  FileText,
  Mail,
  Receipt,
  ShieldCheck,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { SkeletonTableRows } from '../ui/kit';

const STATE_LABELS = {
  calculated: 'Berechnet',
  ready: 'Freigegeben',
  exported: 'An Bankdatei übergeben',
  paid: 'Ausgezahlt',
};

const cents = (value) => `${(Number(value || 0) / 100).toLocaleString('de-DE', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
})} €`;

// PARTNER-LEISTUNG-EINNAHMEN-UBER-001: `carry_out_cents` wird als POSITIVE Schuld
// gespeichert (backend/src/lib/financeLedger.js:1838) und kommt in der Folgewoche als
// NEGATIVER `carry_in` zurück (:1835). Der Schlusssaldo ist deshalb −carry_out — sonst
// stünde derselbe Betrag auf zwei Bildschirmen mit verschiedenem Vorzeichen. Die 0 wird
// angezeigt, nicht unterdrückt; `-0` würde sonst als „-0,00 €" erscheinen.
const gedreht = (value) => ((Number(value) || 0) === 0 ? 0 : -Number(value));

function saveBlob(response, filename, type) {
  const url = window.URL.createObjectURL(new Blob([response.data], { type }));
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  setTimeout(() => window.URL.revokeObjectURL(url), 60000);
}

export default function Invoices() {
  const [periods, setPeriods] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/partners/settlements')
      .then((response) => setPeriods(response.data.periods || []))
      .catch(() => toast.error('Abrechnungshistorie konnte nicht geladen werden'))
      .finally(() => setLoading(false));
  }, []);

  const download = async (period, path, filename, type = 'application/pdf') => {
    try {
      const response = await api.get(`/partners/payouts/${period.id}/${path}`, { responseType: 'blob' });
      saveBlob(response, filename, type);
    } catch (error) {
      toast.error('Datei konnte nicht geladen werden');
    }
  };

  const emailDocument = async (documentId) => {
    try {
      await api.post(`/finance/documents/${documentId}/email`);
      toast.success('Sicherer Link wurde an die verifizierte Konto-E-Mail gesendet');
    } catch (error) {
      toast.error(error.response?.data?.error === 'email_not_verified'
        ? 'Bitte zuerst die Konto-E-Mail bestätigen'
        : 'E-Mail-Link konnte nicht gesendet werden');
    }
  };

  return (
    <div>
      <div style={{ marginBottom: 22 }}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: '#9C27B0', fontSize: 12, fontWeight: 800, letterSpacing: 0.7, textTransform: 'uppercase' }}>
          <ShieldCheck size={13} /> Unveränderliche Finanzstände
        </span>
        <h1 style={{ fontSize: 'var(--vg-fs-title)', fontWeight: 900, letterSpacing: -0.7, margin: '6px 0' }}>Abrechnung</h1>
        <p style={{ color: '#4b5563', fontSize: 13, margin: 0 }}>
          Umsatz- und Auszahlungsübersicht, separate VOOGO-Gebührenrechnung und Partnerdetail-CSV.
        </p>
      </div>

      <div style={{ border: '1px solid #374151', borderRadius: 12, background: '#0a0a0a', padding: 14, marginBottom: 18, display: 'flex', gap: 10, alignItems: 'flex-start' }}>
        <Receipt size={17} color="#9C27B0" />
        <div>
          <strong style={{ color: '#f3f4f6', fontSize: 13 }}>Zwei getrennte Dokumente</strong>
          <p style={{ color: '#d1d5db', fontSize: 12, lineHeight: 1.55, margin: '3px 0 0' }}>
            Die Umsatz- und Auszahlungsübersicht ist ausdrücklich keine Rechnung. Die Rechnung betrifft nur die eigenen VOOGO-Leistungen; Zahlungsdienstleisterkosten sind neutral und separat ausgewiesen.
          </p>
        </div>
      </div>

      <div className="pp-dark-finance-table" style={{ overflowX: 'auto', border: '1px solid #374151', borderRadius: 12 }}>
        <table style={{ width: '100%', minWidth: 980, borderCollapse: 'collapse', background: '#0a0a0a' }}>
          {/* PARTNER-LEISTUNG-EINNAHMEN-UBER-001 (P1-1): Die Spaltenköpfe heißen wortgleich
              wie der verbindliche Beleg (backend/src/lib/financeDocuments.js:212-214,238)
              UND wie die Einnahmen-Seite. „Kartengebühren" hieß dieselbe Zahl nur hier —
              und war zu eng, weil das Feld auch die Kosten aus Guthabenkäufen enthält
              (financeDocuments.js:228-231). `data-spalte` trägt den reinen Namen, damit
              die Erklärzeile den Kopf nicht verlängert. */}
          <thead><tr style={{ textAlign: 'left' }}>
            {['Zeitraum', 'Fahrten', 'Bar', 'Online', 'Auszahlungsbetrag', 'Schlusssaldo', 'Zahlungsdienstleisterkosten', 'Status', 'Dokumente'].map((heading) => (
              <th key={heading} data-spalte={heading} style={{ padding: '12px 14px', fontSize: 12, color: '#d1d5db', fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0.4 }}>
                {heading}
                {heading === 'Zahlungsdienstleisterkosten' && (
                  <small style={{ display: 'block', marginTop: 4, fontSize: 11, fontWeight: 600, color: '#d1d5db', textTransform: 'none', letterSpacing: 0 }}>
                    Was der Zahlungsdienstleister für den Einzug berechnet.
                  </small>
                )}
              </th>
            ))}
          </tr></thead>
          <tbody>
            {loading && <SkeletonTableRows rows={4} cols={9} />}
            {!loading && periods.length === 0 && (
              <tr><td colSpan={9} style={{ padding: 42, textAlign: 'center', color: '#d1d5db' }}>Noch keine freigegebenen Wochenabrechnungen.</td></tr>
            )}
            {!loading && periods.map((period) => {
              const paid = period.state === 'paid';
              const providerCosts = period.payment_provider_costs;
              const providerCostCents = providerCosts?.total_cents ?? period.psp_cost_cents;
              return (
                <tr key={period.id} style={{ borderTop: '1px solid #374151' }}>
                  <td style={{ padding: 14, fontSize: 12, color: '#d1d5db' }}>
                    <strong style={{ display: 'block', color: '#f3f4f6' }}>{period.period_label}</strong>
                    <small style={{ display: 'block', color: '#d1d5db' }}>Europe/Berlin</small>
                  </td>
                  <td style={{ padding: 14, color: '#d1d5db', fontSize: 13 }}>{period.ride_count}</td>
                  <td style={{ padding: 14, color: '#d1d5db', fontSize: 13 }}>{cents(period.cash_cents)}</td>
                  <td style={{ padding: 14, color: '#d1d5db', fontSize: 13 }}>{cents(period.online_cents)}</td>
                  <td style={{ padding: 14, fontSize: 16, color: '#d8b4e2', fontWeight: 900 }}>{cents(period.payout_cents)}</td>
                  <td style={{ padding: 14, color: '#d1d5db', fontSize: 13 }}>{cents(gedreht(period.carry_out_cents))}</td>
                  <td style={{ padding: 14, color: '#d1d5db', fontSize: 13 }}>
                    {cents(gedreht(providerCostCents))}
                    {providerCosts && <>
                      <small style={{ display: 'block' }}>Karte/Stripe {providerCosts.stripe?.transaction_count || 0} · {cents(gedreht(providerCosts.stripe?.fee_cents))}</small>
                      <small style={{ display: 'block' }}>PayPal {providerCosts.paypal?.transaction_count || 0} · {cents(gedreht(providerCosts.paypal?.fee_cents))}</small>
                    </>}
                  </td>
                  <td style={{ padding: 14 }}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12, fontWeight: 800, color: paid ? '#d8b4e2' : '#d1d5db', background: paid ? '#24142a' : '#1f2937', borderRadius: 20, padding: '4px 9px' }}>
                      {paid ? <CheckCircle2 size={11} /> : <Clock3 size={11} />} {STATE_LABELS[period.state] || period.state}
                    </span>
                    {period.batch_id && <small style={{ display: 'block', color: '#d1d5db', marginTop: 5 }}>Batch {period.batch_id}</small>}
                  </td>
                  <td style={{ padding: 14 }}>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                      <button type="button" onClick={() => download(period, 'statement', `Umsatzuebersicht-${period.statement_number}.pdf`)} style={buttonStyle}>
                        <Receipt size={12} /> Übersicht
                      </button>
                      <button type="button" onClick={() => download(period, 'invoice', `VOOGO-Gebuehrenrechnung-${period.fee_invoice_number}.pdf`)} style={buttonStyle}>
                        <FileText size={12} /> Gebührenrechnung
                      </button>
                      <button type="button" onClick={() => download(period, 'detail.csv', `Partnerdetail-${period.id}.csv`, 'text/csv;charset=utf-8')} style={buttonStyle}>
                        <FileSpreadsheet size={12} /> CSV
                      </button>
                      <button type="button" onClick={() => emailDocument(period.statement_document_id)} style={buttonStyle}>
                        <Mail size={12} /> Übersicht mailen
                      </button>
                      <button type="button" onClick={() => emailDocument(period.fee_invoice_document_id)} style={buttonStyle}>
                        <Mail size={12} /> Rechnung mailen
                      </button>
                    </div>
                    <small style={{ display: 'flex', gap: 4, alignItems: 'center', marginTop: 7, color: '#d1d5db' }}>
                      <Download size={10} /> Zugriff wird revisionssicher protokolliert
                    </small>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const buttonStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 5,
  padding: '7px 10px',
  background: '#1f2937',
  color: '#f3f4f6',
  border: '1px solid #4b5563',
  borderRadius: 7,
  fontSize: 12,
  fontWeight: 700,
  cursor: 'pointer',
};
